import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 16000-16999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const commentsErrors = {
  // 16xxx is a fresh block for the Comments module (packages/server/comments/) — split off the
  // generic RESOURCE_NOT_FOUND (9003), same methodology as the accounts-module split above. Comments
  // back three unrelated thread kinds (issue/task/clash-checklist) sharing one collection, hence the
  // separate concepts below rather than one blanket "not found".
  //
  // The stored comment row itself could not be found (by _id, on any of: edit, delete, toggle
  // reaction, toggle official).
  COMMENT_NOT_FOUND: {
    errorCode: 16001,
    errorMessage: t("Comment not found."),
    isDynamic: false,
  },
  // The comment exists but is soft-deleted (recordStatus DELETED); the edit's own scoped filter
  // matched nothing for that reason specifically, distinct from the comment never having existed.
  COMMENT_ALREADY_DELETED: {
    errorCode: 16002,
    errorMessage: t("Cannot edit a deleted comment"),
    isDynamic: false,
  },
  // The clash-test row or checklist-question id a new comment names could not be resolved. Distinct
  // from ISSUE_NOT_FOUND/TASK_NOT_FOUND — the clash/checklist thread has no permission check of its
  // own and no dedicated resource lookup elsewhere in the codebase to reuse.
  COMMENT_RESOURCE_NOT_FOUND: {
    errorCode: 16003,
    errorMessage: t("The resource this comment belongs to was not found."),
    isDynamic: false,
  },
  // getCommentAttachment: the requested fileId is not a valid uuid, or names no live (non-tombstoned)
  // comment's attachment. Not ownership-checked — see comments/CLAUDE.md's §7 on this route's
  // known gap.
  COMMENT_ATTACHMENT_NOT_FOUND: {
    errorCode: 16004,
    errorMessage: t("Attachment not found"),
    isDynamic: false,
  },
  // A Tasks row a comment references (commenting on a task, or the shared checkTaskPermission gate)
  // does not exist. Scoped here because it originates from the comments module's own task lookups;
  // reusable if/when the tasks module's own RESOURCE_NOT_FOUND sites get their turn.
  TASK_NOT_FOUND: {
    errorCode: 16005,
    errorMessage: t("Task not found"),
    isDynamic: false,
  },
  COMMENT_RESOURCE_ID_REQUIRED_CHECKLIST: {
    errorCode: 16006,
    errorMessage: t("resourceId is required for a checklist comment."),
    isDynamic: false,
  },
  COMMENT_RESOURCE_ID_REQUIRED_TASK: {
    errorCode: 16007,
    errorMessage: t("resourceId is required to comment on a task."),
    isDynamic: false,
  },
  // 16008-16010: the reply-threading refusals, which had no code.
  COMMENT_PARENT_NOT_ON_ISSUE: {
    errorCode: 16008,
    errorMessage: t("Parent comment not found on this issue"),
    isDynamic: false,
  },
  COMMENT_CANNOT_REPLY_TO_REPLY: {
    errorCode: 16009,
    errorMessage: t("Cannot reply to a reply"),
    isDynamic: false,
  },
  COMMENT_CANNOT_REPLY_TO_DELETED: {
    errorCode: 16010,
    errorMessage: t("Cannot reply to a deleted comment"),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
